import * as STD            from '@emersya/mpa-std-lib';

class Manager {
	static isInitialized = false;
	static isInitializing = false;
	//Too cumberstone to check all times if root is not null
	//Furthermore the check should be done only one times and at the launch so do like that
	static root = <DOMComponent<any, any, any>> <any> null;
	//Too cumberstone to check all times if root is not null
	//Furthermore the check should be done only one times and at the launch so do like that
	static manifest = <Manifest.ManagerManifest> <any> null;
	static storeTypes : Map<string, Component.TAnyConcrete> = new Map;
	static storeInstances : Set<Component.TAny> = new Set;
	static storeInstancesByType : Map<Component.TAnyConcrete, Component.TAny[]> = new Map;
	static componentStaticProxySymbol = Symbol('component-static-proxy');
	private static declaredComponentBeforeInitialization = <Manager.IDeclareDescriptor<any>[]> [];
	static componentStaticProxy = {
		//declare construct as member of Component to use protecteds / privates members
		construct : (async function construct(
			this : Component<any, any>,
			target : Component.TAnyConcrete,
			args : unknown[]
		) {
			let component : Component<any, any> | null = null;
			try {
				component = new target(...args);
				await component.component_finalize();
				return component;
			} catch (e) {
				if (component) {
					throw STD.errors.bindFromUnknown(
						`Cannot instantiate component<${ component.component_getPath() }>!`,
						{
							target,
							args,
							component
						},
						e
					);
				}
				const desc = <Component.IDescriptor> args[ 0 ];
				const path = (desc.parent ? (desc.parent.component_getPath(true) + '.') : '') + desc.id;
				throw STD.errors.bindFromUnknown(
					'Cannot instantiate a component!',
					{
						target,
						args,
						path
					},
					e
				);
			}
		}).bind(null as any as Component<any, any>),  //eslint-disable-line no-extra-bind
		get(
			target : Component.TAnyConcrete,
			propertyName : string
		) : unknown {
			const v0 = (<any> target)[ propertyName ];
			if (v0 !== undefined) {
				return v0;
			}
			for (const t of STD.object.getInheritanceChainFromClass(target).slice(1)) {
				const v1 = (<any> t)[ propertyName ];
				if (v1) {
					return v1;
				}
			}
			return;
		}
	};

	static declareAbstract<T extends Component.TAbstract>(
		desc : Manager.IDeclareAbstractDescriptor<T>
	)
	{
		try {
			if (!this.isInitialized && !this.isInitializing) {
				this.declaredComponentBeforeInitialization.push(desc);
				return;
			}
			desc.type.type = desc.id;
			const Class = desc.type;
			Class.declarationDescriptor = desc;
			this.storeTypes.set(desc.id, <Component.TAnyConcrete> <any> Class);
			this.storeInstancesByType.set(<Component.TAnyConcrete> <any> Class, []);
			return Class as typeof Class;
		} catch (e) {
			throw STD.errors.bindFromUnknown('Cannot declare a component!', {
				descriptor : desc
			}, e);
		}
	};


	static declare<T extends Component.TAnyConcrete>(
		desc : Manager.IDeclareDescriptor<T>
	)
	{
		try {
			if (!this.isInitialized && !this.isInitializing) {
				this.declaredComponentBeforeInitialization.push(desc);
				return;
			}
			desc.type.type = desc.id;
			const Class = new Proxy(desc.type, this.componentStaticProxy);
			(<any> desc.type)[ this.componentStaticProxySymbol ] = Class;
			Class.declarationDescriptor = desc;
			this.storeTypes.set(desc.id, Class);
			this.storeInstancesByType.set(Class, []);
			return Class as typeof Class;
		} catch (e) {
			throw STD.errors.bindFromUnknown('Cannot declare a component!', {
				descriptor : desc
			}, e);
		}
	};

	static initialize(_triggerRender? : () => void) {
		this.isInitializing = true;
		for (const toDeclare of this.declaredComponentBeforeInitialization) {
			this.declare(toDeclare);
		}
		this.isInitialized = true;
		this.isInitializing = false;
	};

	static async instantiate(desc : Manager.IInstantiateDescriptor) {
		const C = this.storeTypes.get(desc.type);
		if (!C) {
			throw STD.errors.user(
				`Manager.instantiate: Unknown type<${ desc.type }>`,
				{
					descriptor : desc
				}
			);
		}
		if (!C.hasOwnProperty('isFullyDeclared') || !C.isFullyDeclared) {
			C.finalizeDeclaration();
		}
		const realDescriptor : Component.IDescriptor = {
			type :         desc.type,
			id :           desc.id,
			parent :       desc.parent || null,
			isSlot :       desc.isSlot,
			isRoot :       desc.isRoot,
			tree :         desc.tree,
			declaredSlot : desc.declaredSlot
		};
		const c = await (<Promise<Component<any, any>>> <any> new C(realDescriptor, desc.configuration));
		return c;
	};

	static setManifest(newManifest : Manifest.ManagerManifest) {
		if (this.manifest) {
			throw STD.errors.user('Manifest already defined!', {
				currentManifest : this.manifest,
				newManifest
			});
		}
		this.manifest = newManifest;
	};

	static setRoot(newRoot : DOMComponent<any, any, any>) {
		if (this.root) {
			throw STD.errors.user('Root already defined!', {
				currentRoot : this.root,
				newRoot
			});
		}
		this.root = newRoot;
	};

	static generatePropertyEvaluateVariables(
		origin : Component<any, any>,
		context : Condition.TCondition | STD.events.Event<any, Component<any, any>>,
		target : Component<any, any>
	)
	{
		return {
			ORIGIN :    origin,
			TARGET :    target,
			EVENT :     (context instanceof STD.events.Event) ? context : null,
			CONDITION : !(context instanceof STD.events.Event) ? context : null
		};
	};

	static getClassProxyOrThrow(className : string) {
		const ComponentProxy = this.storeTypes.get(className);
		if (!ComponentProxy) {
			throw STD.errors.user('');
		}
		return ComponentProxy;
	};

	// This algorithm is incomprehensible!!!
	static mapFormalEventActionParameterToEffective(
		event : STD.events.Event<any, Component<any, any>>,
		parameterValue : any,
		host : Component<any, any>
	) : unknown {
		//return as it if it's not an object or is nulled
		if (!parameterValue || (typeof parameterValue !== 'object')) {
			return parameterValue;
		}
		//Return subvalues remapped
		if (parameterValue instanceof Array) {
			return parameterValue.map(
				x => this.mapFormalEventActionParameterToEffective(event, x, host)
			);
		}
		//return one value
		if (parameterValue.$ !== undefined) {
			//return the STD.events.Event directly
			if (parameterValue.$ === null) {
				return event;
			}
			//return the value hosted on the STD.events.Event as formal value representing the path
			return STD.object.PropertyPath.evaluate(parameterValue.$, event);
		}
		//return one value
		if (parameterValue[ '@' ] === null) {
			return host;
		}
		//return a real object, if it contains property where their id match the pattern "$.MyId"
		//MyId would be used as property name
		return Object.entries(parameterValue).reduce((out, [ k, v ]) => {
			if (k.startsWith('$.')) {
				k = k.slice(2);
				if (typeof v !== 'string') {
					throw STD.errors.user('');
				}
				v = STD.object.PropertyPath.evaluate(v, event);
			} else {
				v = this.mapFormalEventActionParameterToEffective(event, v, host);
			}
			out[ k ] = v;
			return out;
		}, <Record<any, any>> {});
	};

	static getComponentsPathAndInstanceCouples() : [ Path.TPathRaw, Component<any, any> ][] {
		return Array.from(this.storeInstances).map(x => [ x.component_getPath(), x ]);
	};

	private constructor() {
		STD.errors.user('Manager class is static and so is not instantiable!');
	};
};
namespace Manager {
	export import DOMComponent = DOMComponentNS;
	export import Component = ComponentNS;
	export import Manifest = ManifestNS;
	export import Path = PathNS;
	export import Condition = ConditionNS;
	export import PropertyOperation = PropertyOperationNS;
	export import Plugin = PluginNS;
	export type IInstantiateDescriptor = Omit<
		Component.IDescriptor, 'parent'
	> & {
		configuration : Component.IConfiguration;
		parent? : STD.object.PropertyType<Component.IDescriptor, 'parent'>;
	};


	export interface IDeclareAbstractDescriptor<T extends Component.TAbstract> {
		id : string;
		type : T;
		manifest? : string;
	};

	export interface IDeclareDescriptor<T extends Component.TAnyConcrete> {
		id : string;
		type : T;
		manifest? : string;
	};

}
;
// Import for export
import DOMComponentNS      from './DOMComponent';
import ComponentNS         from './Component';
import ManifestNS          from './Manifest';
import PathNS              from './Path';
import ConditionNS         from './Condition';
import PropertyOperationNS from './PropertyOperation';
import PluginNS            from './Plugin';
//  Imports for use

export default Manager;
import DOMComponent        from './DOMComponent';
import Component           from './Component';
import Manifest            from './Manifest';
import Path                from './Path';
import Condition           from './Condition';
import './manifests/ManagerManifest';
import './manifests/ComponentManifest';
// export interface IEventPath {
// 	id : string;
// 	path : ComponentPath.TComponentPathRaw;
// };
// export type TAction = string;
// export type TSlotId = string | null;
